/**
 * normalizers.js - Per-vertical data normalisation.
 *
 * Each normaliser adds `_n` (normalised numeric) fields so sorting
 * and comparison never need to parse strings at render time.
 * Missing values → Infinity (sorts to bottom) or null.
 */

// ── Parsing helpers ──────────────────────────────────────────

/** Extract first numeric value from a string. Returns null if none found. */
function parseNum(v) {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  const m = String(v).match(/-?\d[\d,]*(\.[\d]+)?/);
  return m ? parseFloat(m[0].replace(/,/g, '')) : null;
}

/** Safe number for sorting - missing → Infinity (bottom). */
function sortNum(v, fallback = Infinity) {
  const n = typeof v === 'number' ? v : parseNum(v);
  return n != null && isFinite(n) ? n : fallback;
}

// ── Per-vertical normaliser functions ────────────────────────

function normaliseBankAccount(item) {
  return {
    ...item,
    _n: {
      monthly_fee_cad: sortNum(item.fees?.monthly_fee, Infinity),
      interest_rate_percent: sortNum(item.interest?.rate_percent, -Infinity),
      welcome_bonus_cad: sortNum(item.welcome_bonus?.value_cad, -Infinity),
      cashback_percent: sortNum(item.features?.cashback_percent, -Infinity),
      finmaple_score: sortNum(item.ranking?.finmaple_score, -Infinity),
    },
  };
}

function normaliseMortgage(item) {
  return {
    ...item,
    _n: {
      down_payment_percent: sortNum(item.down_payment?.minimum_percent, Infinity),
      down_payment_no_credit_percent: sortNum(item.down_payment?.minimum_percent_no_credit, Infinity),
      rate_guarantee_days: sortNum(item.rate_guarantee_days, -Infinity),
      min_employment_months: sortNum(item.credit_requirements?.min_employment_months, Infinity),
      finmaple_score: sortNum(item.ranking?.finmaple_score, -Infinity),
    },
  };
}

function normaliseRemittance(item) {
  return {
    ...item,
    _n: {
      fee_cad: sortNum(parseNum(item.fee_structure), Infinity),
      finmaple_score: sortNum(item.ranking?.finmaple_score, -Infinity),
    },
  };
}

function normaliseRegisteredAccount(item) {
  return {
    ...item,
    _n: {
      contribution_limit_2026: sortNum(item.contribution_limit_2026, -Infinity),
      finmaple_score: sortNum(item.ranking?.finmaple_score, -Infinity),
    },
  };
}

function normaliseInsurance(item) {
  return {
    ...item,
    _n: {
      mandatory: item.mandatory ? 1 : 0,
      finmaple_score: sortNum(item.ranking?.finmaple_score, -Infinity),
    },
  };
}

// Identity normaliser for verticals that don't need numeric fields (GICs)
function identity(item) {
  return item;
}

// ── Normaliser registry ──────────────────────────────────────

const NORMALISER_MAP = {
  bank_accounts: normaliseBankAccount,
  mortgages: normaliseMortgage,
  remittances: normaliseRemittance,
  registered_accounts: normaliseRegisteredAccount,
  insurance: normaliseInsurance,
  gics: identity,
};

/**
 * Normalise an array of items for a given vertical key.
 * @param {string} verticalKey  e.g. 'bank_accounts'
 * @param {Array} items         Raw items from JSON
 * @returns {Array}             Normalised items with _n fields
 */
export function normaliseItems(verticalKey, items) {
  const fn = NORMALISER_MAP[verticalKey] || identity;
  return items.map(fn);
}

// ── Dev-only validation ──────────────────────────────────────

const REQUIRED_FIELDS = {
  _common: ['id', 'ranking'],
  bank_accounts: ['name', 'institution', 'fees', 'interest'],
  mortgages: ['program_name', 'lender', 'down_payment'],
  remittances: ['name', 'type'],
  registered_accounts: ['name', 'type'],
  insurance: ['name', 'type'],
  gics: [],
};

/**
 * Dev-only: log warnings for items missing required fields.
 * Only runs when NODE_ENV === 'development'.
 */
export function validateVerticalData(verticalKey, items) {
  if (process.env.NODE_ENV !== 'development') return;

  const common = REQUIRED_FIELDS._common || [];
  const vertical = REQUIRED_FIELDS[verticalKey] || [];
  const required = [...common, ...vertical];

  items.forEach((item, idx) => {
    required.forEach((field) => {
      // support dotted paths like "ranking"
      const val = field.includes('.')
        ? field.split('.').reduce((o, k) => o?.[k], item)
        : item[field];
      if (val == null || val === '') {
        console.warn(
          `[validateVerticalData] ${verticalKey}[${idx}] (${item.id || 'unknown'}): missing required field "${field}"`
        );
      }
    });

    // Score range check
    const score = item.ranking?.finmaple_score;
    if (score != null && (score < 0 || score > 100)) {
      console.warn(
        `[validateVerticalData] ${verticalKey}[${idx}] (${item.id}): score ${score} out of range 0–100`
      );
    }
  });
}
